import { Visibility, VisibilityOff } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import { Alert, List, ListItem } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthApi from '../api/AuthApi';
import {
	AUTHORIZATION_INVALID_STATUS_CODES,
	NOT_FOUND_ERRORS,
} from '../api/http';
import LogoBlueSvg from '../assets/socotra-blue.svg';
import { ROLES_AND_PERMISSION_ENABLED, useAuthContext } from '../context/auth';
import {
	HOSTNAME_LABEL,
	INVALID_AUTHORIZATION_MESSAGE,
	INVALID_AUTHORIZATION_REQUIRED_PERMISSIONS_TITLE_MESSAGE,
	INVALID_HOSTNAME_MESSAGE,
	INVALID_LOGIN_MESSAGE,
	LOGIN_ADMIN,
	LOGIN_ADMIN_LOGIN,
	LOGIN_CLOSE,
	LOGIN_CONFIG_MESSAGE,
	LOGIN_EMPTY_HOSTNAME,
	LOGIN_EMPTY_PASSWORD,
	LOGIN_EMPTY_USERNAME,
	LOGIN_FORGOT_PASSWORD,
	LOGIN_PASSWORD,
	LOGIN_PASSWORD_RECOVER,
	LOGIN_SESSION_EXPIRED_TEXT,
	LOGIN_SSO_INFO,
	LOGIN_SSO_LEARN,
	LOGIN_STANDARD_LOGIN,
	LOGIN_SWITCH_TEXT,
	LOGIN_WELCOME,
	PASSWORD_LABEL,
	USERNAME_LABEL,
} from '../data/labelsAndMessages';
import { loadFrontEndIntegrations } from '../fe-plugins';
import { Permission } from '../permissions';
import { getErrorCodeFromMessage } from '../util/util';
import { TenantResponse } from './admin/tenant/api/tenants';
import LargeCenterSpinner from './LargeCenterSpinner';

interface State {
	username: string;
	password: string;
	hostName: string;
	submitted: boolean;
	missingPermissions: Permission[];
	showUnauthorizedLoginMessage: boolean;
	showInvalidLoginMessage: boolean;
	showInvalidHostnameMessage: boolean;
	showInvalidAdminMessage: boolean;
	authResponse: any;
	disabledFFs: string[];
	value: number;
	open: boolean;
	showPassword: boolean;
	loading: boolean;
	searchParams: URLSearchParams;
}

interface LoginData {
	username: string;
	password: string;
	hostName?: string;
}

const formControlStyle = { width: '100%' };
const formFieldStyle = { width: '100%' };
const formLabelStyle = { fontWeight: '600', marginTop: '8px' };

const InvalidLoginMessage = () => {
	return (
		<FormHelperText id='Login__Message--Invalid' error>
			{INVALID_LOGIN_MESSAGE()}
		</FormHelperText>
	);
};

const InvalidHostnameMessage = () => {
	return (
		<FormHelperText id='Login__Message--Invalid-Hostname' error>
			{INVALID_HOSTNAME_MESSAGE()}
		</FormHelperText>
	);
};

interface UnauthorizedLoginMessageProps {
	missingPermissions: Permission[];
}

const UnauthorizedLoginMessage = (props: UnauthorizedLoginMessageProps) => {
	const { missingPermissions } = props;
	return (
		<Alert severity='error'>
			<Stack id='Login__Message--Unauthorized' spacing={2}>
				<Typography variant='body2'>
					{INVALID_AUTHORIZATION_MESSAGE()}
				</Typography>
				<Typography variant='body2'>
					{INVALID_AUTHORIZATION_REQUIRED_PERMISSIONS_TITLE_MESSAGE()}
				</Typography>
				<List disablePadding>
					{missingPermissions.map((permissions, index) => (
						<ListItem key={index}>{permissions}</ListItem>
					))}
				</List>
			</Stack>
		</Alert>
	);
};

const qsInitialValue = (key: string, defaultValue = ''): string => {
	const search: string = window.location.search;
	const params = new URLSearchParams(search);
	// first check if hostname is present in url
	if (params.get(key)) {
		return params.get(key);
		// if no tenantHostname in url, check localStorage for most recent tenant
	} else if (localStorage.getItem('currentTenant')) {
		return JSON.parse(localStorage.getItem('currentTenant')).hostname;
		// otherwise, set to empty
	} else {
		return defaultValue;
	}
};

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role='tabpanel'
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ p: 3 }}>
					<Typography component='span'>{children}</Typography>
				</Box>
			)}
		</div>
	);
}

const Login = () => {
	const authData = useAuthContext();
	const location = useLocation();
	const navigate = useNavigate();
	const [state, setState] = React.useState<State>({
		username: '',
		password: '',
		hostName: qsInitialValue('loginTenantHostname'),
		submitted: false,
		showUnauthorizedLoginMessage: false,
		showInvalidLoginMessage: false,
		showInvalidHostnameMessage: false,
		showInvalidAdminMessage: false,
		authResponse: null as any,
		disabledFFs: [] as string[],
		missingPermissions: [],
		value: 0,
		open: false,
		showPassword: false,
		loading: false,
		searchParams: new URLSearchParams(location.search),
	});

	React.useEffect(() => {
		const authStatus = authData.getAuthStatus();

		if (authStatus === 'VALID') {
			navigate('/home');
		}

		if (authStatus !== 'VALID') {
			if (authStatus === 'EXPIRED') {
				console.error(LOGIN_SESSION_EXPIRED_TEXT());
				authData.clear();
			}
		}
	});

	const handleChange = (newValue: number) => {
		setState((state) => ({ ...state, value: newValue }));
	};

	const handleUserLogin = async (
		event: React.FormEvent<HTMLInputElement>,
	): Promise<void> => {
		const { username, password, hostName, submitted } = state;
		event.preventDefault();

		if (!submitted) {
			setState((state) => ({ ...state, submitted: true }));
		}

		if (!username || !password || !hostName) {
			return;
		}

		try {
			setState((state) => ({ ...state, loading: true }));

			const request: LoginData = {
				username,
				password,
			};

			if (hostName) {
				request.hostName = hostName;
			}

			const response = await AuthApi.authenticateInternal(request);
			const account = await AuthApi.getAccount(
				response.data.accountId,
				response.data.authorizationToken,
			);

			await authData.setPermissions(account.roles);
			const rolesAndPermissionEnabled =
				response.data.configProperties[ROLES_AND_PERMISSION_ENABLED];

			const missingPermissions: Permission[] = [];

			!authData.hasPermission('product.fetch', rolesAndPermissionEnabled) &&
				missingPermissions.push('product.fetch');
			!authData.hasPermission('ontology.fetch', rolesAndPermissionEnabled) &&
				missingPermissions.push('ontology.fetch');
			!authData.hasPermission('workflow.fetch', rolesAndPermissionEnabled) &&
				missingPermissions.push('workflow.fetch');
			!authData.hasPermission('tenant.fetch', rolesAndPermissionEnabled) &&
				missingPermissions.push('tenant.fetch');

			if (rolesAndPermissionEnabled && missingPermissions.length > 0) {
				setState((state) => ({
					...state,
					showUnauthorizedLoginMessage: true,
					missingPermissions,
					loading: false,
				}));
				return;
			}

			const tenantData = {
				locator: response.data.tenantId,
				hostname: hostName,
			};
			localStorage.setItem('currentTenant', JSON.stringify(tenantData));
			setState((state) => ({ ...state, showInvalidLoginMessage: false }));
			authData.setAuth(response.data, request.username, request.hostName);
			loadFrontEndIntegrations(window.document);
		} catch (error) {
			console.error(error);

			const errorCode = getErrorCodeFromMessage(error.message);

			if (errorCode === AUTHORIZATION_INVALID_STATUS_CODES[0]) {
				//username and password are incorrect
				setState((state) => ({
					...state,
					showInvalidLoginMessage: true,
					showInvalidHostnameMessage: false,
				}));
			}

			if (errorCode === NOT_FOUND_ERRORS[0]) {
				//hostname is incorrect
				setState((state) => ({
					...state,
					showInvalidHostnameMessage: true,
				}));
			}
		}

		setState((state) => ({ ...state, loading: false }));
	};

	const handleAdminLogin = async (
		event: React.FormEvent<HTMLInputElement>,
	): Promise<void> => {
		const { username, password, hostName, submitted } = state;
		event.preventDefault();

		if (!submitted) {
			setState((state) => ({ ...state, submitted: true }));
		}

		if (!username || !password || !hostName) {
			return;
		}

		try {
			setState((state) => ({ ...state, loading: true }));

			const request: LoginData = {
				username,
				password,
			};

			const response = await AuthApi.authenticateAdmin(request);
			const tenantLocatorResp = await AuthApi.getTenantId(hostName);
			const currTenant: TenantResponse = {
				locator: tenantLocatorResp.tenantId,
				hostname: hostName,
			};
			localStorage.setItem('currentTenant', JSON.stringify(currTenant));

			setState((state) => ({
				...state,
				showInvalidLoginMessage: false,
			}));
			authData.setAdminAuth(response.data);

			loadFrontEndIntegrations(window.document);
		} catch (error) {
			setState((state) => ({
				...state,
				showInvalidAdminMessage: true,
			}));
			console.error(error);
		}

		setState((state) => ({ ...state, loading: false }));
	};

	const StandardLoginMessage = () => {
		const {
			showInvalidLoginMessage,
			showInvalidHostnameMessage,
			showUnauthorizedLoginMessage,
		} = state;

		if (
			!showInvalidLoginMessage &&
			!showInvalidHostnameMessage &&
			!showUnauthorizedLoginMessage
		) {
			return null;
		}

		if (showUnauthorizedLoginMessage) {
			return (
				<UnauthorizedLoginMessage
					missingPermissions={state.missingPermissions}
				/>
			);
		}

		if (showInvalidHostnameMessage) {
			return <InvalidHostnameMessage />;
		}

		return <InvalidLoginMessage />;
	};

	const LoginTabMessage = () => {
		const { value } = state;
		if (value === 0) {
			return (
				<Box sx={{ textAlign: 'left', mb: 3 }}>
					{LOGIN_SWITCH_TEXT()}
					<Link
						sx={{ color: 'primary.lighter', pl: 1 }}
						onClick={() => handleChange(1)}
					>
						{LOGIN_ADMIN()}
					</Link>
				</Box>
			);
		} else {
			return (
				<Box sx={{ textAlign: 'left', mb: 3, display: 'flex' }}>
					<Box sx={{ display: 'inline', pr: 2, cursor: 'pointer' }}>
						<ArrowBackIcon
							sx={{ color: 'primary.lighter' }}
							onClick={() => handleChange(0)}
						/>
					</Box>
					{LOGIN_ADMIN()}
				</Box>
			);
		}
	};

	const handleOpen = () => setState((state) => ({ ...state, open: true }));
	const handleClose = () => setState((state) => ({ ...state, open: false }));

	const handleClickShowPassword = () => {
		setState((state) => ({ ...state, showPassword: !state.showPassword }));
	};
	const handleMouseDownPassword = (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		event.preventDefault();
	};

	const {
		showInvalidLoginMessage,
		showInvalidHostnameMessage,
		showInvalidAdminMessage,
		username,
		password,
		hostName,
		submitted,
		value,
		showPassword,
		loading,
	} = state;

	// if the tenant does not have QNB or PUREQ flags enabled, render a modal warning the user
	if (authData.ssoEnabled) {
		return <LargeCenterSpinner />;
	}

	return (
		<Box
			className='container'
			sx={{
				height: '100%',
				width: '100%',
				backgroundColor: 'background.default',
			}}
		>
			<Box sx={{ textAlign: 'center', m: 7 }}>
				<LogoBlueSvg style={{ height: '180px' }} />
			</Box>
			<Box
				sx={{
					m: 6,
					mx: 'auto',
					p: 2,
					pt: 3,
					width: '400px',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					backgroundColor: 'white.lightest',
					borderRadius: 2,
					border: 1,
					borderColor: 'white.main',
				}}
			>
				<Box sx={{ width: '100%' }}>
					<TabPanel value={value} index={0}>
						<LoginTabMessage />
						<form id='Login__Form--Standard' onSubmit={handleUserLogin as any}>
							<Stack spacing={2.5}>
								<Typography component='span' variant='caption'>
									{LOGIN_WELCOME()}
								</Typography>
								<TextField
									id='LoginForm__UsernameField--Standard'
									variant='outlined'
									label={USERNAME_LABEL()}
									style={formFieldStyle}
									size='small'
									onChange={(e: any) =>
										setState((state) => ({
											...state,
											username: e.target.value,
										}))
									}
									value={username}
									error={(submitted && !username) || showInvalidLoginMessage}
									helperText={
										submitted && !username ? LOGIN_EMPTY_USERNAME() : null
									}
								/>
								<FormControl
									id='LoginForm__Password--Standard'
									style={formControlStyle}
									variant='outlined'
									size='small'
								>
									<InputLabel
										htmlFor='LoginForm__PasswordField--Standard'
										error={(submitted && !password) || showInvalidLoginMessage}
									>
										{LOGIN_PASSWORD()}
									</InputLabel>
									<OutlinedInput
										id='LoginForm__PasswordField--Standard'
										label={PASSWORD_LABEL()}
										style={formFieldStyle}
										type={showPassword ? 'text' : 'password'}
										value={password}
										onChange={(e: any) =>
											setState((state) => ({
												...state,
												password: e.target.value,
											}))
										}
										error={(submitted && !password) || showInvalidLoginMessage}
										endAdornment={
											<InputAdornment position='end'>
												<IconButton
													id='LoginForm__PasswordVisibilityToggle--Standard'
													aria-label='toggle password visibility'
													onClick={handleClickShowPassword}
													onMouseDown={handleMouseDownPassword}
													edge='end'
												>
													{showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										}
									/>
									{submitted && !password && (
										<FormHelperText
											id='LoginForm__PasswordHelperText--Standard'
											error={submitted && !password}
										>
											{LOGIN_EMPTY_PASSWORD()}
										</FormHelperText>
									)}
									<Link
										id='LoginForm__ForgotPassword--Standard'
										color='secondary'
										variant='caption'
										align='right'
										onClick={handleOpen}
										style={formLabelStyle}
									>
										{LOGIN_FORGOT_PASSWORD()}
									</Link>
								</FormControl>
								<FormControl
									id='LoginForm__Hostname--Standard'
									style={formControlStyle}
									variant='outlined'
									size='small'
								>
									<TextField
										id='LoginForm__HostnameField--Standard'
										variant='outlined'
										label={HOSTNAME_LABEL()}
										style={formFieldStyle}
										size='small'
										name='hostName'
										onChange={(e: any) =>
											setState((state) => ({
												...state,
												hostName: e.target.value,
											}))
										}
										value={hostName}
										error={
											(submitted && !hostName) || showInvalidHostnameMessage
										}
										helperText={
											submitted && !hostName ? LOGIN_EMPTY_HOSTNAME() : null
										}
										InputProps={{
											startAdornment: (
												<InputAdornment
													id='LoginForm__HostnamePrefix--Standard'
													position='start'
												>
													https://
												</InputAdornment>
											),
										}}
									/>
									{!submitted || hostName ? (
										<FormHelperText
											id='LoginForm__HostnameHelperText--Standard'
											sx={{ mx: 0 }}
										>
											{LOGIN_SSO_INFO()}{' '}
											{
												<a
													target='_blank'
													rel='noopener noreferrer'
													href='https://docs.socotra.com/production/tooling/single-sign-on.html?highlight=sso'
												>
													{LOGIN_SSO_LEARN()}
												</a>
											}
										</FormHelperText>
									) : null}
								</FormControl>

								<StandardLoginMessage />

								<Button
									id='LoginForm__Button--StandardLogin'
									color='secondary'
									variant='contained'
									fullWidth
									type='submit'
								>
									{!loading ? (
										LOGIN_STANDARD_LOGIN()
									) : (
										<CircularProgress size={25} sx={{ color: 'white' }} />
									)}
								</Button>
							</Stack>
						</form>
					</TabPanel>

					<TabPanel value={value} index={1}>
						<LoginTabMessage />
						<form id='Login__Form--Admin' onSubmit={handleAdminLogin as any}>
							<Stack spacing={2.5}>
								<Typography component='span' variant='caption'>
									{LOGIN_CONFIG_MESSAGE()}
								</Typography>
								<TextField
									id='LoginForm__UsernameField--Admin'
									variant='outlined'
									label={USERNAME_LABEL()}
									style={formFieldStyle}
									size='small'
									onChange={(e: any) =>
										setState((state) => ({
											...state,
											username: e.target.value,
										}))
									}
									value={username}
									error={(submitted && !username) || showInvalidAdminMessage}
									helperText={
										submitted && !username ? LOGIN_EMPTY_USERNAME() : null
									}
								/>
								<FormControl
									id='LoginForm__Password--Admin'
									style={formControlStyle}
									variant='outlined'
									size='small'
								>
									<InputLabel
										htmlFor='LoginForm__PasswordField--Admin'
										error={(submitted && !password) || showInvalidAdminMessage}
									>
										{LOGIN_PASSWORD()}
									</InputLabel>
									<OutlinedInput
										id='LoginForm__PasswordField--Admin'
										label={PASSWORD_LABEL()}
										style={formFieldStyle}
										type={showPassword ? 'text' : 'password'}
										value={password}
										onChange={(e: any) =>
											setState((state) => ({
												...state,
												password: e.target.value,
											}))
										}
										error={(submitted && !password) || showInvalidAdminMessage}
										endAdornment={
											<InputAdornment position='end'>
												<IconButton
													id='LoginForm__PasswordVisibilityToggle--Admin'
													aria-label='toggle admin password visibility'
													onClick={handleClickShowPassword}
													onMouseDown={handleMouseDownPassword}
													edge='end'
												>
													{showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										}
									/>
									{submitted && !password && (
										<FormHelperText
											id='LoginForm__PasswordHelperText--Admin'
											error={submitted && !password}
										>
											{LOGIN_EMPTY_PASSWORD()}
										</FormHelperText>
									)}
									<Link
										id='LoginForm__ForgotPassword--Admin'
										color='secondary'
										variant='caption'
										align='right'
										onClick={handleOpen}
										style={formLabelStyle}
									>
										{LOGIN_FORGOT_PASSWORD()}
									</Link>
								</FormControl>
								<FormControl
									id='LoginForm__Hostname--Admin'
									style={formControlStyle}
									variant='outlined'
									size='small'
								>
									<TextField
										id='LoginForm__HostnameField--Admin'
										variant='outlined'
										label={HOSTNAME_LABEL()}
										style={formFieldStyle}
										size='small'
										name='hostName'
										onChange={(e: any) =>
											setState((state) => ({
												...state,
												hostName: e.target.value,
											}))
										}
										value={hostName}
										error={(submitted && !hostName) || showInvalidAdminMessage}
										helperText={
											submitted && !hostName ? LOGIN_EMPTY_HOSTNAME() : null
										}
										InputProps={{
											startAdornment: (
												<InputAdornment
													id='LoginForm__HostnamePrefix--Admin'
													position='start'
												>
													https://
												</InputAdornment>
											),
										}}
									/>
									{!submitted || hostName ? (
										<FormHelperText
											id='LoginForm__HostnameHelperText--Admin'
											sx={{ mx: 0 }}
										>
											{LOGIN_SSO_INFO()}{' '}
											{
												<a
													target='_blank'
													rel='noopener noreferrer'
													href='https://docs.socotra.com/production/tooling/single-sign-on.html?highlight=sso'
												>
													{LOGIN_SSO_LEARN()}
												</a>
											}
										</FormHelperText>
									) : null}
								</FormControl>
								{showInvalidAdminMessage ? <InvalidLoginMessage /> : null}
								<Button
									id='LoginForm__Button--AdminLogin'
									color='secondary'
									variant='contained'
									fullWidth
									type='submit'
									startIcon={!loading && <LockIcon />}
								>
									{!loading ? (
										LOGIN_ADMIN_LOGIN()
									) : (
										<CircularProgress size={25} sx={{ color: 'white' }} />
									)}
								</Button>
							</Stack>
						</form>
					</TabPanel>
				</Box>

				<Dialog
					id='Login__Dialogue'
					open={state.open}
					onClose={handleClose}
					aria-labelledby='Login__Dialogue--Title'
					aria-describedby='Login__Dialogue--Description'
				>
					<DialogTitle id='Login__Dialogue--Title'>
						{LOGIN_FORGOT_PASSWORD()}
					</DialogTitle>
					<DialogContent>
						<DialogContentText id='Login__Dialogue--Description'>
							{LOGIN_PASSWORD_RECOVER()}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose}>{LOGIN_CLOSE()}</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</Box>
	);
};

export default Login;
